<template>
    <Transition :name="transitionName" mode="out-in" @after-enter="focusMfaInput">
        <Form
            v-if="showMfaForm"
            ref="mfaFormRef"
            key="mfa"
            :class="formClass"
            :schema="TotpCodeSchema"
            :form-values="mfaForm"
            :is-busy="isMfaBusy"
            @submit="onMfaSubmit">
            <p class="text-base">
                {{ $t("Security.TOTPCodeSignIn") }}
            </p>

            <Alert v-if="errorMessage" variant="danger">
                <AlertDescription>{{ errorMessage }}</AlertDescription>
            </Alert>
            <slot v-else />

            <FormPinInput
                v-model="mfaForm.CodeDigits"
                field="CodeDigits"
                :label="$t('Security.VerificationCode')"
                otp />

            <template #actions="{ isBusy: mfaBusy, isValid: mfaValid }">
                <Button type="submit" variant="primary" fill="solid" size="lg" :disabled="mfaBusy || !mfaValid">
                    <Spinner v-if="mfaBusy" variant="white" size="xs" />
                    <span>{{ $t("Security.Verify") }}</span>
                </Button>

                <Button variant="default" size="lg" @click="resetForm">
                    <Icon name="chevron-left" />
                    <span>{{ $t("Register.ReturnToSignIn") }}</span>
                </Button>
            </template>
        </Form>
        <Form
            v-else
            key="login"
            :class="formClass"
            :schema="LoginSchema"
            :form-values="loginForm"
            :is-busy="isLoginBusy"
            :submit-text="signInButtonText"
            @submit="onLoginSubmit"
            @reset="resetForm">
            <Alert v-if="errorMessage" variant="danger">
                <AlertDescription>{{ errorMessage }}</AlertDescription>
            </Alert>
            <slot v-else />

            <FormInput v-model="loginForm.UserName" :label="$t('SignIn.UserNameLabelText')" field="UserName" />

            <FormInput
                v-model="loginForm.Password"
                :label="$t('Global.PasswordLabel')"
                type="password"
                field="Password" />

            <div>
                <a :href="localePath('/forgot-password')">{{ $t("SignIn.ForgotPasswordText") }}</a>
            </div>

            <div v-if="!isAuthenticated">
                {{ $t("SignIn.CreateAccount") }}
                <a :href="localePath('/register')">{{ $t("SignIn.SignupLink") }}</a>
            </div>
        </Form>
    </Transition>
</template>
<script setup lang="ts">
import type { HTMLAttributes } from "vue";

const { t } = useI18n();
const guestCookie = useGuestCookie();
const { logIn, verifyMfa } = useIdentity();
const initializeState = useInitializeState();
const { importAnonymousBOM } = useBom();
const localePath = useLangPath();
const { LoginSchema, TotpCodeSchema } = useSchemas();
const route = useRoute();
const loginStatus = useStateLoginStatus();
const isAuthenticated = useStateIsAuthenticated();

const loginForm = ref<LogInRequest>({ UserName: "", Password: "" });
const errorMessage = ref("");
const isLoginBusy = ref(false);
const isMfaBusy = ref(false);
const showMfaForm = ref(false);
const transitionDirection = ref<"forward" | "backward">("forward");
const transitionName = computed(() => `form-slide-${transitionDirection.value}`);
const mfaFormRef = useTemplateRef<{ $el: HTMLElement }>("mfaFormRef");
const mfaState = ref({ Session: "", UserName: "" });
const mfaForm = ref<TotpForm>({ CodeDigits: [] });

const { class: formClass = "" } = defineProps<{ class?: HTMLAttributes["class"] }>();

const emit = defineEmits<{ success: [response: LogInResponse]; complete: [response: LogInResponse] }>();

const signInButtonText = computed(() => t(isLoginBusy.value ? "SignIn.LoginButtonLoading" : "Global.SignInLabel"));

function focusMfaInput() {
    if (!showMfaForm.value) return;
    mfaFormRef.value?.$el?.querySelector<HTMLInputElement>("input")?.focus();
}

async function onLoginSubmit(logInRequest: LogInRequest) {
    isLoginBusy.value = true;
    errorMessage.value = "";
    loginStatus.value = LoginStatus.Pending;

    try {
        // Handle both PascalCase and camelCase query parameter formats for compatibility
        const returnUrl = route.query.ReturnUrl?.toString() ?? route.query.returnUrl?.toString();
        const logInResponse = await logIn(logInRequest, returnUrl);

        // Check if MFA is required
        if (logInResponse.RequiresMfa) {
            mfaState.value = { Session: logInResponse.Session, UserName: logInRequest.UserName };
            mfaForm.value = { CodeDigits: [] };
            transitionDirection.value = "forward";
            showMfaForm.value = true;
        } else {
            finishLogin(logInResponse);
        }
    } catch (err) {
        loginStatus.value = LoginStatus.Error;
        const error = err as Error;
        errorMessage.value = error.message || t("Global.GenericError");
    } finally {
        isLoginBusy.value = false;
    }
}

async function onMfaSubmit(values: { CodeDigits: number[] }) {
    const { CodeDigits: digits } = values;
    if (!digits) return;

    isMfaBusy.value = true;
    errorMessage.value = "";
    loginStatus.value = LoginStatus.Pending;

    try {
        const returnUrl = route.query.ReturnUrl?.toString() ?? route.query.returnUrl?.toString();
        const logInResponse = await verifyMfa({ ...mfaState.value, MfaCode: digits.join("") }, returnUrl);

        await finishLogin(logInResponse);
    } catch (err) {
        loginStatus.value = LoginStatus.Error;
        const error = err as Error;
        errorMessage.value = error.message;
    } finally {
        isMfaBusy.value = false;
    }
}

async function finishLogin(logInResponse: LogInResponse) {
    emit("success", logInResponse);
    loginStatus.value = LoginStatus.Success;

    // Only initialize state if the user was previously not authenticated
    // Remember, this could be used by already-authenticated users (e.g. my-account/security)
    if (!isAuthenticated.value) {
        await initializeState();
        await importAnonymousBOM();

        guestCookie.value = null;
    }

    emit("complete", logInResponse);
    loginStatus.value = LoginStatus.Complete;
}

function resetForm() {
    loginForm.value = { UserName: "", Password: "" };
    errorMessage.value = "";
    loginStatus.value = LoginStatus.None;
    mfaState.value = { Session: "", UserName: "" };
    mfaForm.value = { CodeDigits: [] };
    transitionDirection.value = "backward";
    showMfaForm.value = false;
}
</script>

<style scoped>
/* Forward: login → MFA */
.form-slide-forward-enter-active,
.form-slide-forward-leave-active,
/* Backward: MFA → login */
.form-slide-backward-enter-active,
.form-slide-backward-leave-active {
    transition:
        opacity 0.28s ease,
        transform 0.28s ease;
}

.form-slide-forward-enter-from {
    opacity: 0;
    transform: translateX(calc(var(--spacing) * 6));
}
.form-slide-forward-leave-to {
    opacity: 0;
    transform: translateX(calc(var(--spacing) * -6));
}

.form-slide-backward-enter-from {
    opacity: 0;
    transform: translateX(calc(var(--spacing) * -6));
}
.form-slide-backward-leave-to {
    opacity: 0;
    transform: translateX(calc(var(--spacing) * 6));
}
</style>
